import { lazy, Suspense, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  Calendar, User, Wallet as WalletIcon, ShoppingBag, ReceiptText,
  ShieldCheck, LogOut, LineChart,
} from 'lucide-react';
import { useTenant, TenantLogo, isBareDomain } from '@fitboxx/shared';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import Login from './pages/Login';
import Schedule from './pages/Schedule';
import MyBookings from './pages/MyBookings';
import Me from './pages/Me';
import Wallet from './pages/Wallet';
import Shop from './pages/Shop';
import Invoices from './pages/Invoices';
import Agreements from './pages/Agreements';

// Only the bare domain draws it — kept out of the members' bundle.
const FitLanding = lazy(() => import('./pages/FitLanding'));
import Progress from './pages/Progress';

const NAV = [
  { to: '/', label: 'Schedule', icon: Calendar },
  { to: '/shop', label: 'Shop', icon: ShoppingBag },
  { to: '/progress', label: 'Progress', icon: LineChart },
  { to: '/wallet', label: 'Wallet', icon: WalletIcon },
  { to: '/me', label: 'Me', icon: User },
];

export default function App() {
  const { tenant, loading } = useTenant();
  const [hasToken, setHasToken] = useState(!!localStorage.getItem('fitboxx.member.access'));

  useEffect(() => {
    function onStorage() { setHasToken(!!localStorage.getItem('fitboxx.member.access')); }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // fitboxxone.com draws FitBOXX's own landing page (owner, 2026-10-05).
  if (isBareDomain()) return <Suspense fallback={null}><FitLanding /></Suspense>;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Skeleton className="h-24 w-72" />
      </div>
    );
  }
  // No studio at this address → no sign-in, ever (owner, 2026-10-04). This
  // used to come after the token gate, so a login showed on any address.
  if (!tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
        No studio found at this address. Use the link your studio gave you.
      </div>
    );
  }
  if (!hasToken) return <Login onLoggedIn={() => setHasToken(true)} />;

  return (
    <div className="min-h-screen bg-background pb-24">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 py-4">
        <Routes>
          <Route path="/" element={<Schedule />} />
          <Route path="/bookings" element={<MyBookings />} />
          <Route path="/me" element={<Me />} />
          <Route path="/progress" element={<Progress />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/agreements" element={<Agreements />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <BottomNav />
    </div>
  );
}

function TopBar() {
  const { tenant } = useTenant();
  const name = tenant?.display_name || 'FitBOXX';
  return (
    <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b">
      <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
        <TenantLogo tenant={tenant} size={30} className="shrink-0" />
        <div className="font-semibold tracking-tight truncate">{name}</div>
        <div className="ml-auto flex items-center gap-2">
          <NavLink to="/me" className="rounded-full">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="text-xs">ME</AvatarFallback>
            </Avatar>
          </NavLink>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { localStorage.removeItem('fitboxx.member.access'); location.reload(); }}
            title="Sign out"
            className="text-muted-foreground"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}

function BottomNav() {
  const loc = useLocation();
  return (
    <nav className="fixed bottom-0 inset-x-0 z-40 bg-card border-t">
      <div className="max-w-3xl mx-auto grid grid-cols-5">
        {NAV.map(({ to, label, icon: Icon }) => {
          const active = to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(to);
          return (
            <NavLink
              key={to}
              to={to}
              className={cn(
                'flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-medium',
                active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground transition-colors',
              )}
            >
              <Icon className={cn('h-5 w-5', active && 'text-primary')} />
              {label}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

// Re-exported for cross-page navigation use if needed.
export { ReceiptText, ShieldCheck };
